import { IconMailForward, IconUserCheck, IconUserPlus, IconUsers, IconX } from "@tabler/icons-react";
import { useState, type FormEvent } from "react";
import { api, ApiError, type TripMember } from "../api";
import { useSession } from "../session";
import { Card, ErrorNote, Field } from "./ui";

/**
 * The trip's members (docs/16 decision 14): holder and companions, invited or joined. The holder invites by email and
 * removes companions; a companion sees the list only. Companions get the plan, the documents and the notices.
 */
export function CompanionsCard({ tripId, members: initial, holder, holderName }: { tripId: string; members: TripMember[]; holder: boolean; holderName: string | null }) {
  const { t } = useSession();
  const [members, setMembers] = useState<TripMember[]>(initial);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const errorText = (err: unknown) => {
    if (err instanceof ApiError) {
      if (err.code === "already_member") return t("errAlreadyMember");
      if (err.code === "too_many") return t("errTooManyCompanions");
      if (err.code === "holder_only") return t("errHolderOnlyCompanions");
    }
    return t("somethingWrong");
  };

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address || busy) return;
    setBusy("invite");
    setError(null);
    try {
      const res = await api.inviteMember(tripId, { email: address, ...(name.trim() ? { name: name.trim() } : {}) });
      setMembers((list) => [...list.filter((m) => m.id !== res.member.id), res.member]);
      setEmail("");
      setName("");
      setOpen(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (m: TripMember) => {
    if (busy) return;
    if (!window.confirm(t("removeCompanionConfirm", { name: m.name ?? m.email ?? "" }))) return;
    setBusy(m.id);
    setError(null);
    try {
      await api.removeMember(tripId, m.id);
      setMembers((list) => list.filter((x) => x.id !== m.id));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const rows = members.length > 0 || !holderName ? members : [{ id: "holder", name: holderName, email: null, role: "holder", joinedAt: null } satisfies TripMember];

  return (
    <Card className="companions-card">
      <p className="small">{holder ? t("companionsBody") : t("companionsReadOnly")}</p>
      <ul className="list">
        {rows.map((m) => {
          const isHolder = m.role === "holder";
          const joined = isHolder || Boolean(m.joinedAt);
          return (
            <li key={m.id} className="list-row">
              <span className="list-icon">{isHolder ? <IconUsers size={20} stroke={1.5} /> : joined ? <IconUserCheck size={20} stroke={1.5} /> : <IconMailForward size={20} stroke={1.5} />}</span>
              <span className="list-text">
                <strong>{m.name ?? m.email ?? "—"}</strong>
                <span className="muted small">
                  {isHolder ? t("holder") : t("companion")}
                  {m.name && m.email ? ` · ${m.email}` : ""}
                </span>
              </span>
              {!isHolder && <span className={`chip ${joined ? "chip-joined" : "chip-invited"}`}>{joined ? t("joined") : t("invited")}</span>}
              {holder && !isHolder && (
                <button type="button" className="icon-button" aria-label={t("remove")} title={t("remove")} disabled={busy !== null} onClick={() => void remove(m)}>
                  <IconX size={16} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {error && <ErrorNote>{error}</ErrorNote>}
      {holder &&
        (open ? (
          <form className="form companion-form" onSubmit={(e) => void invite(e)}>
            <div className="form-grid">
              <Field label={t("email")}>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("emailPlaceholder")} autoComplete="off" required autoFocus />
              </Field>
              <Field label={t("nameOptional")}>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" />
              </Field>
            </div>
            <div className="row-buttons">
              <button type="submit" className="button button-primary button-small" disabled={busy !== null || !email.trim()}>
                {busy === "invite" ? t("inviting") : t("invite")}
              </button>
              <button type="button" className="link-button" onClick={() => setOpen(false)}>
                {t("cancel")}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <button type="button" className="button button-small" onClick={() => setOpen(true)}>
              <IconUserPlus size={16} /> {t("inviteCompanion")}
            </button>
          </div>
        ))}
    </Card>
  );
}
